import { Button } from '@bambi/component-library/src/components/Button/Button';
import { SimpleTooltip } from '@bambi/component-library/src/components/Tooltip/SimpleTooltip';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { tripSlice } from '../../trip.slice';
import EditTripMenuItem from './EditTripMenuItem';
import { useFeature } from 'src/app/FeatureToggle';
import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { DropdownMenuContent } from 'src/common/DropdownMenu/DropdownMenuContent';
import { DropdownMenuItem } from 'src/common/DropdownMenu/DropdownMenuItem';
import type {
  InvoiceTripsRead,
  OverrideStatusEnum,
  TripRead,
} from 'src/common/external/bambi-api/bambiApi';
import type { TripListRead } from 'src/common/external/bambi-api/tripsApi';
import { useLazyTripsWithSummaryListQuery } from 'src/common/external/bambi-api/tripsApi';
import { useCanCapturePaymentMethod } from 'src/common/forms/PaymentMethodCaptureForm/useCanCapturePaymentMethod';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';
import { useUserRoles } from 'src/features/auth/useUserRoles';
import { useCreateHydratedInvoice } from 'src/features/billing/invoices/management/useCreateEmptyInvoice';
import { useEditInvoiceById } from 'src/features/billing/invoices/management/useEditInvoice';
import { useTripMenuActions } from 'src/features/dispatch/DispatchCalendar/useTripMenuActions';
import { setOpenTripDropdownMenuId } from 'src/features/dispatch/dispatcher.slice';
import { CancellationMenu } from 'src/features/dispatch/menus/TripDropdownCancellationMenu';
import { useShouldShowRideOrderingPortalFeatures } from 'src/features/rideOrderingPortal/useShouldShowRideOrderingPortalFeatures';
import { isWillCallScheduledForToday } from 'src/features/trip/will-call/isWillCallScheduledForToday';

interface TripGridRowActionsProps {
  trip: TripRead | TripListRead;
  triggerClassName?: string;
}

export const TripGridRowActions = ({
  trip,
  triggerClassName,
}: TripGridRowActionsProps) => {
  const dispatch = useAppDispatch();
  const openTripDropdownMenuId = useAppSelector(
    (state) => state.dispatcher.openTripDropdownMenuId
  );

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (openTripDropdownMenuId === trip.id) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [openTripDropdownMenuId, trip.id]);

  return (
    <div className="w-[24px]">
      <DropdownMenu.Root
        open={open}
        onOpenChange={(val) => {
          if (!val) dispatch(setOpenTripDropdownMenuId(null));
        }}
      >
        <DropdownMenu.Trigger asChild>
          <Button
            className={triggerClassName}
            variant="ghost"
            dataTestId="row-actions-trigger"
            onClick={() => dispatch(setOpenTripDropdownMenuId(trip.id))}
          >
            <EllipsisVerticalIcon className="h-6 w-6 cursor-pointer" />
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenuContent
            side="bottom"
            data-testid="trip-grid-row-actions-menu"
          >
            <TripGridRowActionsMenuItems trip={trip} />
          </DropdownMenuContent>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
};

function DeleteTripMenuItem({ trip }: { trip: TripRead | TripListRead }) {
  const dispatch = useAppDispatch();
  const isBrokerTrip = !!trip.intermediary_id;

  // TODO: Need a generic disabled tooltip configuration
  // DropdownMenuItem/Button swallow hover events when disabled
  // Button at least allows me to nest SimpleTooltip so I emulate the styles here
  if (isBrokerTrip) {
    return (
      <Button
        variant="ghost"
        disabled
        className="px-1 py-0"
        dataTestId="delete-trip-menu-item"
      >
        <SimpleTooltip
          trigger={
            <span data-testid="broker-delete-tooltip-trigger">Delete</span>
          }
          content={
            <span>This trip is a broker trip and cannot be deleted.</span>
          }
        />
      </Button>
    );
  }

  return (
    <DropdownMenuItem
      onClick={() => dispatch(tripSlice.actions.onDeleteTripAction(trip))}
    >
      Delete
    </DropdownMenuItem>
  );
}

function TripGridRowActionsMenuItems({
  trip,
}: {
  trip: TripRead | TripListRead;
}) {
  const dispatch = useAppDispatch();
  const { isRideOrderingPortalUser } = useUserRoles();

  const shouldShowRideOrderingPortalFeatures =
    useShouldShowRideOrderingPortalFeatures();

  const menuActions = useTripMenuActions();
  const {
    isLoading: isPaymentFeatureLoading,
    isEnabled: isPaymentFeatureEnabled,
    onlyInvoicingEnabled,
  } = useCanCapturePaymentMethod();
  const handleCreateEmptyInvoice = useCreateHydratedInvoice();
  const handleEditInvoice = useEditInvoiceById();
  const tripHasInvoice = !!trip.invoice_id;

  const [fetchTripWithPriceSummary] = useLazyTripsWithSummaryListQuery();
  const fetchTripWithPriceSummaryThenCreateInvoice = useCallback(async () => {
    try {
      const { results } = await fetchTripWithPriceSummary({
        idIn: [trip.id],
      }).unwrap();

      const tripWithSummary = results[0];

      handleCreateEmptyInvoice({
        line_items: [
          {
            description: `Trip - ${trip.id}`,
            amount_cents: tripWithSummary.input_price_cents ?? 0,
            trip: tripWithSummary as unknown as InvoiceTripsRead,
            line_item_type: 'trip',
          },
          {
            description: '',
            amount_cents: tripWithSummary.input_price_cents ?? 0,
            trip: tripWithSummary as unknown as InvoiceTripsRead,
            line_item_type: 'price_summary',
            price_summary: tripWithSummary.price_summary,
          },
        ],
      });
    } catch (e) {
      dispatch(
        show({
          type: 'error',
          title: 'Unable to retrieve trip for invoice',
          description: formatServerError(e),
        })
      );
    }
  }, [dispatch, fetchTripWithPriceSummary, handleCreateEmptyInvoice, trip]);
  const { isEnabled: isFacilityTripCancelEnabled } = useFeature(
    'facility trip cancel'
  );

  const showCancelTripDropdownItem = useMemo(() => {
    const validStatuses: OverrideStatusEnum[] = [
      'requested',
      'pending',
      'assigned',
      'en route',
      'at pickup',
    ];

    if (!isRideOrderingPortalUser) {
      return true;
    }

    return validStatuses.includes(trip.status) && isFacilityTripCancelEnabled;
  }, [isRideOrderingPortalUser, trip.status, isFacilityTripCancelEnabled]);

  // Show a limited menu for deleted trips
  if (trip.deleted_at && !shouldShowRideOrderingPortalFeatures) {
    return (
      <DropdownMenuItem onClick={() => menuActions.goToTripSummary(trip.id)}>
        View Summary
      </DropdownMenuItem>
    );
  }

  return (
    <>
      <DropdownMenuItem onClick={() => menuActions.clone(trip.id)}>
        Clone
      </DropdownMenuItem>
      <EditTripMenuItem trip={trip} onViewEdit={menuActions.viewEdit} />
      {showCancelTripDropdownItem && <CancellationMenu trip={trip} />}
      {!shouldShowRideOrderingPortalFeatures && (
        <>
          <DropdownMenuItem onClick={() => menuActions.addEditTags(trip.id)}>
            Add Tags
          </DropdownMenuItem>

          <DeleteTripMenuItem trip={trip} />

          <DropdownMenuItem
            onClick={() => menuActions.goToTripSummary(trip.id)}
          >
            View Summary
          </DropdownMenuItem>
        </>
      )}
      {!isRideOrderingPortalUser &&
        trip.is_will_call &&
        !trip.is_will_call_activated &&
        isWillCallScheduledForToday(trip.scheduled_pickup_at) && (
          // TODO: Resolve the differences between ActivateWillCallItem and this
          // Like... just pass the id instead of the whole trip object
          // and avoid the DispatchTripRead vs TripRead type shenanigans
          <DropdownMenuItem
            onClick={() => menuActions.activateWillCall({ tripId: trip.id })}
          >
            Activate will call
          </DropdownMenuItem>
        )}
      {!isPaymentFeatureLoading &&
        (isPaymentFeatureEnabled || onlyInvoicingEnabled) && (
          <DropdownMenuItem
            onClick={() => {
              if (tripHasInvoice && trip.invoice_id) {
                handleEditInvoice(trip.invoice_id);
              } else {
                fetchTripWithPriceSummaryThenCreateInvoice();
              }
            }}
          >
            {tripHasInvoice ? 'Edit Invoice' : 'Create Invoice'}
          </DropdownMenuItem>
        )}
      <DropdownMenuItem
        onClick={() => menuActions.createSubscriptionFromTrip(trip.id)}
      >
        Create Subscription
      </DropdownMenuItem>
      {!(trip.status === 'requested' || trip.status === 'pending') && (
        <DropdownMenuItem
          onClick={() => menuActions.editStatusUpdates(trip.id)}
        >
          Edit Status Updates
        </DropdownMenuItem>
      )}
    </>
  );
}
